import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building2, HardHat, Wrench, LayoutDashboard, FileText, Database,
  AlertCircle, Activity, FolderOpen, RefreshCw, ArrowLeft, Bot,
  Plus, TrendingUp, Clock, CheckCircle2, XCircle, DollarSign,
  MessageSquare, ChevronRight, Filter, Search, MoreVertical,
  ExternalLink, Trash2, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';

const BRONZE = '#C9A961';

// Division configuration
const DIVISION_CONFIG = {
  government: {
    name: 'Government Contracting',
    icon: Building2,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    pipelineLabel: 'Opportunities',
    statuses: [
      { value: 'new', label: 'New', color: 'bg-blue-500' },
      { value: 'qualified', label: 'Qualified', color: 'bg-purple-500' },
      { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-500' },
      { value: 'submitted', label: 'Submitted', color: 'bg-orange-500' },
      { value: 'won', label: 'Won', color: 'bg-green-500' },
      { value: 'lost', label: 'Lost', color: 'bg-red-500' }
    ]
  },
  gc: {
    name: 'General Contracting',
    icon: HardHat,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/30',
    pipelineLabel: 'Bid Pipeline',
    statuses: [
      { value: 'invited', label: 'Invited', color: 'bg-blue-500' },
      { value: 'takeoff', label: 'Takeoff', color: 'bg-purple-500' },
      { value: 'pricing', label: 'Pricing', color: 'bg-yellow-500' },
      { value: 'proposal_ready', label: 'Proposal Ready', color: 'bg-orange-500' },
      { value: 'sent', label: 'Sent', color: 'bg-cyan-500' },
      { value: 'awarded', label: 'Awarded', color: 'bg-green-500' },
      { value: 'lost', label: 'Lost', color: 'bg-red-500' }
    ]
  },
  fm: {
    name: 'Facility Maintenance',
    icon: Wrench,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/30',
    pipelineLabel: 'Work Orders',
    statuses: [
      { value: 'new', label: 'Received', color: 'bg-blue-500' },
      { value: 'dispatched', label: 'Dispatched', color: 'bg-purple-500' },
      { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-500' },
      { value: 'completed', label: 'Completed', color: 'bg-green-500' },
      { value: 'invoicing', label: 'Invoicing', color: 'bg-orange-500' },
      { value: 'paid', label: 'Paid', color: 'bg-emerald-500' }
    ]
  }
};

const DivisionWorkspace = () => {
  const { divisionId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [portals, setPortals] = useState([]);
  const [waitingOnMe, setWaitingOnMe] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showAddPortalDialog, setShowAddPortalDialog] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatChannel, setChatChannel] = useState(null);
  const [newPortal, setNewPortal] = useState({
    portal_name: '',
    portal_url: '',
    login_method: 'user_pass',
    description: '',
    check_frequency: 'daily'
  });

  const config = DIVISION_CONFIG[divisionId] || DIVISION_CONFIG.government;
  const Icon = config.icon;

  useEffect(() => {
    fetchAllData();
    fetchChatData();
  }, [divisionId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statsRes, oppsRes, portalsRes, womRes, activityRes, docsRes] = await Promise.all([
        api.get(`/command-center/stats/${divisionId}`),
        api.get(`/command-center/opportunities?division_id=${divisionId}&limit=100`),
        api.get(`/command-center/portals?division_id=${divisionId}`),
        api.get(`/command-center/waiting-on-me?division_id=${divisionId}`),
        api.get(`/command-center/activity-log?division_id=${divisionId}&limit=50`),
        api.get(`/command-center/documents?division_id=${divisionId}`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setOpportunities(oppsRes.data);
      setPortals(portalsRes.data);
      setWaitingOnMe(womRes.data);
      setActivityLog(activityRes.data);
      setDocuments(docsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch chat messages for this division
  const fetchChatData = async () => {
    try {
      // Get the division channel
      const channelsRes = await api.get('/chat/channels');
      const divChannel = channelsRes.data.find(c => c.name === divisionId && c.is_division);
      
      if (divChannel) {
        setChatChannel(divChannel);
        // Get messages for this channel
        const messagesRes = await api.get(`/chat/channels/${divChannel.id}/messages?limit=50`);
        setChatMessages(messagesRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching chat:', error);
    }
  };

  // Send message to division chat
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatChannel) return;
    
    try {
      const response = await api.post(`/chat/channels/${chatChannel.id}/messages`, {
        content: newMessage.trim()
      });
      setChatMessages([...chatMessages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  // Portal management functions
  const handleTogglePortalStatus = async (portal) => {
    try {
      const newStatus = portal.status === 'active' ? 'inactive' : 'active';
      await api.patch(`/command-center/portals/${portal.id}`, { status: newStatus });
      setPortals(portals.map(p => 
        p.id === portal.id ? { ...p, status: newStatus } : p
      ));
      toast.success(`Portal ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error updating portal:', error);
      toast.error('Failed to update portal status');
    }
  };

  const handleDeletePortal = async (portal) => {
    if (!window.confirm(`Are you sure you want to delete "${portal.portal_name}"?`)) return;
    
    try {
      await api.delete(`/command-center/portals/${portal.id}`);
      setPortals(portals.filter(p => p.id !== portal.id));
      toast.success('Portal deleted');
    } catch (error) {
      console.error('Error deleting portal:', error);
      toast.error('Failed to delete portal');
    }
  };

  const handleAddPortal = async () => {
    if (!newPortal.portal_name || !newPortal.portal_url) {
      toast.error('Please fill in portal name and URL');
      return;
    }
    
    try {
      const response = await api.post('/command-center/portals', {
        ...newPortal,
        division_id: divisionId,
        status: 'active'
      });
      setPortals([...portals, response.data]);
      setShowAddPortalDialog(false);
      setNewPortal({
        portal_name: '',
        portal_url: '',
        login_method: 'user_pass',
        description: '',
        check_frequency: 'daily'
      });
      toast.success('Portal added');
    } catch (error) {
      console.error('Error adding portal:', error);
      toast.error('Failed to add portal');
    }
  };

  const getStatusColor = (status) => {
    const statusConfig = config.statuses.find(s => s.value === status);
    return statusConfig?.color || 'bg-gray-500';
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      urgent: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-gray-500 text-white'
    };
    return <Badge className={colors[priority] || 'bg-gray-500'}>{priority}</Badge>;
  };

  // Group opportunities by status for Kanban
  const groupedOpps = config.statuses.reduce((acc, status) => {
    acc[status.value] = opportunities.filter(o => o.status === status.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin" style={{ color: BRONZE }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-6 border-b border-gray-800 bg-black/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/command-center')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-8 w-px bg-gray-800" />
            <img 
              src="/williams-logo-transparent.png" 
              alt="Williams Diversified" 
              className="h-10 w-auto"
            />
            <div className={`p-3 rounded-xl ${config.bgColor}`}>
              <Icon className={`h-8 w-8 ${config.color}`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{config.name}</h1>
              <p className="text-gray-400">Division Workspace</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {(waitingOnMe.length > 0) && (
              <Badge className="bg-red-500/20 text-red-400 border border-red-500/50">
                <AlertCircle className="h-3 w-3 mr-1" />
                {waitingOnMe.length} Waiting
              </Badge>
            )}
            <Button variant="outline" className="border-gray-700" onClick={fetchAllData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
        <div className="border-b border-gray-800 bg-black/30 px-6">
          <TabsList className="bg-transparent border-0 h-14">
            <TabsTrigger 
              value="dashboard" 
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="pipeline"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <FileText className="h-4 w-4 mr-2" />
              Bids
              <Badge className="ml-2 bg-gray-700">{opportunities.length}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="portals"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <Database className="h-4 w-4 mr-2" />
              Portals
              <Badge className="ml-2 bg-gray-700">{portals.length}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="waiting"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Waiting On Me
              {waitingOnMe.length > 0 && (
                <Badge className="ml-2 bg-red-500">{waitingOnMe.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="activity"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <Activity className="h-4 w-4 mr-2" />
              Activity
            </TabsTrigger>
            <TabsTrigger 
              value="documents"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger 
              value="chat"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C9A961] data-[state=active]:text-[#C9A961] rounded-none px-4"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Team Chat
              {chatMessages.length > 0 && (
                <Badge className="ml-2 bg-[#C9A961] text-black">{chatMessages.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="p-6">
          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-0 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-black/50 border-gray-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Bids</p>
                      <p className="text-3xl font-bold text-white">{stats?.total_opportunities || 0}</p>
                    </div>
                    <FileText className="h-10 w-10 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-black/50 border-gray-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Waiting On Me</p>
                      <p className="text-3xl font-bold text-red-400">{stats?.pending_waiting_on_me || 0}</p>
                    </div>
                    <AlertCircle className="h-10 w-10 text-red-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-black/50 border-gray-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Win Rate</p>
                      <p className="text-3xl font-bold" style={{ color: BRONZE }}>{stats?.win_rate || 0}%</p>
                    </div>
                    <TrendingUp className="h-10 w-10" style={{ color: BRONZE }} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bids by Status */}
            <Card className="bg-black/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Bids by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 flex-wrap">
                  {config.statuses.map(status => {
                    const count = groupedOpps[status.value]?.length || 0;
                    return (
                      <div 
                        key={status.value}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-800"
                      >
                        <div className={`w-3 h-3 rounded-full ${status.color}`} />
                        <span className="text-gray-400">{status.label}</span>
                        <span className="font-bold text-white">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Bids & Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-black/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Recent Bids</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px]">
                    {opportunities.slice(0, 5).map(opp => (
                      <div key={opp.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-900/30 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{opp.title}</p>
                          <p className="text-xs text-gray-500">{opp.source_name}</p>
                        </div>
                        <Badge className={`${getStatusColor(opp.status)} text-white ml-2`}>
                          {opp.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                    {opportunities.length === 0 && (
                      <div className="text-center py-8 text-gray-500">No items yet</div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="bg-black/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Bot className="h-5 w-5 text-blue-400" />
                    Recent AI Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px]">
                    {activityLog.slice(0, 5).map((activity, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-900/30 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {activity.actor_type === 'agent' && (
                              <Badge className="bg-blue-500/20 text-blue-400 text-xs">AI</Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {new Date(activity.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-white mt-1">{activity.details || activity.action_type.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                    ))}
                    {activityLog.length === 0 && (
                      <div className="text-center py-8 text-gray-500">No activity yet</div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline" className="mt-0">
            <div className="flex gap-4 overflow-x-auto pb-4">
              {config.statuses.map(status => (
                <div key={status.value} className="flex-shrink-0 w-80">
                  <div className="bg-black/30 rounded-lg border border-gray-800">
                    <div className="p-3 border-b border-gray-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${status.color}`} />
                        <span className="font-medium text-white">{status.label}</span>
                      </div>
                      <Badge variant="outline" className="text-gray-400">
                        {groupedOpps[status.value]?.length || 0}
                      </Badge>
                    </div>
                    
                    <ScrollArea className="h-[calc(100vh-350px)]">
                      <div className="p-2 space-y-2">
                        {(groupedOpps[status.value] || []).map(opp => (
                          <Card key={opp.id} className="bg-gray-900/80 border-gray-700 hover:border-gray-600 cursor-pointer">
                            <CardContent className="p-3">
                              <h4 className="font-medium text-white text-sm line-clamp-2 mb-2">{opp.title}</h4>
                              <p className="text-xs text-gray-500 mb-2">{opp.source_name}</p>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {getPriorityBadge(opp.priority)}
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                {opp.deadline && (
                                  <span><Clock className="h-3 w-3 inline mr-1" />{new Date(opp.deadline).toLocaleDateString()}</span>
                                )}
                                {opp.estimated_value && (
                                  <span>${(opp.estimated_value / 1000).toFixed(0)}K</span>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {(groupedOpps[status.value]?.length === 0) && (
                          <div className="text-center py-8 text-gray-600 text-sm">No items</div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Portals Tab */}
          <TabsContent value="portals" className="mt-0">
            {/* Add Portal Button */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-400 text-sm">{portals.length} portals registered</p>
              <Button
                onClick={() => setShowAddPortalDialog(true)}
                className="bg-[#C9A961] text-black hover:bg-[#B8985A]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Portal
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portals.map(portal => (
                <Card key={portal.id} className="bg-black/50 border-gray-800 hover:border-gray-700 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-white">{portal.portal_name}</h3>
                      <div className="flex items-center gap-2">
                        <Badge 
                          className={`cursor-pointer ${portal.status === 'active' ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'}`}
                          onClick={() => handleTogglePortalStatus(portal)}
                        >
                          {portal.status === 'active' ? (
                            <><ToggleRight className="h-3 w-3 mr-1" />Active</>
                          ) : (
                            <><ToggleLeft className="h-3 w-3 mr-1" />Inactive</>
                          )}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Clickable URL */}
                    <a 
                      href={portal.portal_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-[#C9A961] hover:text-[#B8985A] mb-3 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {portal.portal_url}
                    </a>
                    
                    {portal.description && (
                      <p className="text-xs text-gray-400 mb-3 line-clamp-2">{portal.description}</p>
                    )}
                    
                    <div className="text-xs text-gray-500 mb-3">
                      <p>Login: {portal.login_method?.replace(/_/g, ' ')}</p>
                      <p>Check: {portal.check_frequency}</p>
                    </div>
                    
                    {/* Delete Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeletePortal(portal)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Portal
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {portals.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No portals registered</p>
                  <Button
                    onClick={() => setShowAddPortalDialog(true)}
                    className="mt-4 bg-[#C9A961] text-black hover:bg-[#B8985A]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Portal
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Waiting On Me Tab */}
          <TabsContent value="waiting" className="mt-0">
            {waitingOnMe.length === 0 ? (
              <Card className="bg-black/50 border-gray-800">
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-xl font-semibold text-white mb-2">All Clear!</h3>
                  <p className="text-gray-400">No items waiting for your action in {config.name}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {waitingOnMe.map(item => (
                  <Card key={item.id} className={`bg-black/50 border-gray-800 ${item.priority === 'urgent' ? 'border-l-4 border-l-red-500' : ''}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getPriorityBadge(item.priority)}
                            <Badge variant="outline" className="text-gray-400">
                              {item.request_type.replace(/_/g, ' ')}
                            </Badge>
                            {item.created_by_agent_id && (
                              <Badge className="bg-blue-500/20 text-blue-400">
                                <Bot className="h-3 w-3 mr-1" />AI
                              </Badge>
                            )}
                          </div>
                          <p className="text-white font-medium">{item.summary}</p>
                          {item.recommended_action && (
                            <p className="text-sm text-gray-400 mt-2">
                              <span className="text-gray-500">Recommended:</span> {item.recommended_action}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          {item.deadline && (
                            <p className="text-sm text-gray-500">
                              <Clock className="h-3 w-3 inline mr-1" />
                              {new Date(item.deadline).toLocaleDateString()}
                            </p>
                          )}
                          <Button size="sm" className="mt-2" style={{ backgroundColor: BRONZE, color: 'black' }}>
                            Resolve
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-0">
            <Card className="bg-black/50 border-gray-800">
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="divide-y divide-gray-800">
                    {activityLog.map((activity, idx) => (
                      <div key={idx} className="p-4 hover:bg-gray-900/30">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${activity.actor_type === 'agent' ? 'bg-blue-500/20' : 'bg-gray-800'}`}>
                            {activity.actor_type === 'agent' ? (
                              <Bot className="h-4 w-4 text-blue-400" />
                            ) : (
                              <Activity className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-gray-400 capitalize">
                                {activity.action_type.replace(/_/g, ' ')}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {new Date(activity.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-white">{activity.details || 'No details'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {activityLog.length === 0 && (
                      <div className="text-center py-12 text-gray-500">No activity recorded</div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-0">
            <Card className="bg-black/50 border-gray-800">
              <CardContent className="p-12 text-center">
                <FolderOpen className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-semibold text-white mb-2">Document Vault</h3>
                <p className="text-gray-400 mb-4">Compliance documents, certifications, and more</p>
                <Button style={{ backgroundColor: BRONZE }} className="text-black">
                  <Plus className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Chat Tab */}
          <TabsContent value="chat" className="mt-0">
            <Card className="bg-black/50 border-gray-800 h-[600px] flex flex-col">
              <CardHeader className="border-b border-gray-800 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" style={{ color: BRONZE }} />
                    <CardTitle className="text-white text-lg">#{divisionId} Team Chat</CardTitle>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-gray-400">AI & Humans can communicate here</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/chat?channel=${divisionId}`, `TeamChat_${divisionId}`, 'width=800,height=900,left=100,top=100')}
                      className="text-gray-400 hover:text-[#C9A961]"
                      title="Open in new window"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {/* Messages Area */}
              <ScrollArea className="flex-1 p-4">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-sm">AI agents and team members can communicate here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatMessages.map((msg) => (
                      <div key={msg.id} className={`flex gap-3 ${msg.sender_type === 'agent' ? 'bg-[#C9A961]/10 rounded-lg p-3' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.sender_type === 'agent' 
                            ? 'bg-[#C9A961]/30' 
                            : 'bg-gray-700'
                        }`}>
                          {msg.sender_type === 'agent' ? (
                            <Bot className="h-4 w-4" style={{ color: BRONZE }} />
                          ) : (
                            <span className="text-xs text-white">{msg.sender_name?.[0] || 'U'}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-medium text-sm ${msg.sender_type === 'agent' ? 'text-[#C9A961]' : 'text-white'}`}>
                              {msg.sender_name || 'Unknown'}
                            </span>
                            {msg.sender_type === 'agent' && (
                              <Badge className="bg-[#C9A961]/20 text-[#C9A961] text-xs">AI</Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm break-words">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              {/* Message Input */}
              <div className="p-4 border-t border-gray-800">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder={`Message #${divisionId}...`}
                    className="bg-black/50 border-gray-700 text-white flex-1"
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    style={{ backgroundColor: BRONZE }}
                    className="text-black"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </div>
      </Tabs>

      {/* Add Portal Dialog */}
      <Dialog open={showAddPortalDialog} onOpenChange={setShowAddPortalDialog}>
        <DialogContent className="bg-gray-900 border border-[#C9A961]/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ color: BRONZE }}>Add New Portal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Portal Name *</Label>
              <Input
                value={newPortal.portal_name}
                onChange={(e) => setNewPortal({ ...newPortal, portal_name: e.target.value })}
                placeholder="e.g., Service Channel"
                className="bg-black/50 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Portal URL *</Label>
              <Input
                value={newPortal.portal_url}
                onChange={(e) => setNewPortal({ ...newPortal, portal_url: e.target.value })}
                placeholder="https://example.com"
                className="bg-black/50 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Login Method</Label>
              <Select 
                value={newPortal.login_method} 
                onValueChange={(v) => setNewPortal({ ...newPortal, login_method: v })}
              >
                <SelectTrigger className="bg-black/50 border-gray-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="user_pass">Username & Password</SelectItem>
                  <SelectItem value="sso">Single Sign-On (SSO)</SelectItem>
                  <SelectItem value="api_key">API Key</SelectItem>
                  <SelectItem value="certificate">Certificate</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Check Frequency</Label>
              <Select 
                value={newPortal.check_frequency} 
                onValueChange={(v) => setNewPortal({ ...newPortal, check_frequency: v })}
              >
                <SelectTrigger className="bg-black/50 border-gray-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Description</Label>
              <Textarea
                value={newPortal.description}
                onChange={(e) => setNewPortal({ ...newPortal, description: e.target.value })}
                placeholder="What is this portal used for?"
                className="bg-black/50 border-gray-700 text-white mt-1"
                rows={3}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 border-gray-700 text-gray-300"
                onClick={() => setShowAddPortalDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#C9A961] text-black hover:bg-[#B8985A]"
                onClick={handleAddPortal}
              >
                Add Portal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DivisionWorkspace;
